import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { ROUTES } from '../../../constants/routes';

const NotFound: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-40 w-24 h-24 bg-gray-500/10 rounded-full blur-lg animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-40 w-28 h-28 bg-white/3 rounded-full blur-xl animate-pulse delay-2000"></div>
          <div className="absolute bottom-40 right-20 w-20 h-20 bg-gray-400/8 rounded-full blur-lg animate-pulse delay-500"></div>
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Illustration */}
          <div className="mb-12">
            <div className="relative inline-block">
              {/* Large 404 Text */}
              <div className="text-8xl sm:text-9xl lg:text-[12rem] font-black text-transparent bg-clip-text bg-gradient-to-r from-white/20 to-white/5 leading-none select-none">
                404
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Title */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {t('errors.notFound.title')}
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-white to-gray-400 mx-auto rounded-full"></div>
            </div>

            {/* Description */}
            <div className="space-y-6">
              <p className="text-xl sm:text-2xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                {t('errors.notFound.description')}
              </p>
              
              <p className="text-lg text-gray-400 max-w-xl mx-auto">
                {t('errors.notFound.suggestion')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-white text-black hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl min-w-[200px]"
                >
                  <svg 
                    className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                    />
                  </svg>
                  {t('general.actions.backHome')}
                </Button>
              </Link>

              <button 
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white border-2 border-white/30 rounded-lg hover:border-white/60 hover:bg-white/10 transform hover:scale-105 transition-all duration-300 min-w-[200px]"
              >
                <svg 
                  className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} 
                  />
                </svg>
                {t('general.actions.goBack')}
              </button>
            </div>

            {/* Navigation Links */}
            <div className="pt-12 border-t border-white/10">
              <p className="text-lg text-gray-300 mb-6">
                {t('errors.notFound.explore')}
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <Link 
                  to="/about" 
                  className="group p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {t('nav.about')}
                    </span>
                  </div>
                </Link>

                <Link 
                  to="/ministries" 
                  className="group p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {t('nav.ministries')}
                    </span>
                  </div>
                </Link>

                <Link 
                  to="/events" 
                  className="group p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {t('nav.events')}
                    </span>
                  </div>
                </Link>

                <Link 
                  to={ROUTES.STORE} 
                  className="group p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {t('nav.store')}
                    </span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Footer Message */}
            <div className="pt-8">
              <p className="text-sm text-gray-500 italic">
                {t('errors.notFound.footerMessage')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;